import {
  Button,
  Divider,
  IconButton,
  Icons,
  LayoutCard,
  tokens,
  Typography,
  Stack,
  Box,
} from '@cast/design-system';
import { K8sProviderIcons, ScoreBadge } from '@cast/features-cluster-ui';
import {
  formatClusterDashboardMetadata,
  useClusterDashboardState,
  useClusterDashboardGaugeData,
} from '@cast/features-cluster-shared';
import { useClusterDetailsDrawer } from '../../_hooks/useClusterDetailsDrawer';
import { METADATA_GRID_TEMPLATE_CSS_VAR } from './_constants';
import { KentrollerStatusSection } from './KentrollerStatusSection';
import { MetadataField } from './MetadataField';

const VIEW_ALL_BUTTON_COLUMN_WIDTH = '136px';

const METADATA_GRID_TEMPLATE = `repeat(6, minmax(0, 1fr)) ${VIEW_ALL_BUTTON_COLUMN_WIDTH}`;
const METADATA_GRID_TEMPLATE_WITHOUT_SCORE = `repeat(5, minmax(0, 1fr)) ${VIEW_ALL_BUTTON_COLUMN_WIDTH}`;

export const ClusterMetadataBar = () => {
  const { cluster, isManaged, isKentManaged } = useClusterDashboardState();
  const { openClusterDetailsDrawer } = useClusterDetailsDrawer();
  const metadata = formatClusterDashboardMetadata(cluster);
  const { summary } = useClusterDashboardGaugeData();
  const clusterId = cluster.id;

  const handleCopyClusterId = async () => {
    try {
      await navigator.clipboard.writeText(clusterId);
    } catch {
      // Clipboard may be unavailable in some contexts.
    }
  };

  return (
    <LayoutCard
      testId="cluster-metadata-bar"
      padding
      slotProps={{ root: { sx: { overflow: 'hidden' } } }}
      cssVars={{
        [METADATA_GRID_TEMPLATE_CSS_VAR]: summary?.clusterScore
          ? METADATA_GRID_TEMPLATE
          : METADATA_GRID_TEMPLATE_WITHOUT_SCORE,
      }}
    >
      <Box
        width="100%"
        sx={{
          display: 'grid',
          gridTemplateColumns: `var(${METADATA_GRID_TEMPLATE_CSS_VAR})`,
        }}
      >
        <MetadataField label="PROVIDER">
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography
              variant="P14M"
              sx={{ color: tokens.theme.fg.structural.primary }}
            >
              {metadata.providerLabel}
            </Typography>
            <K8sProviderIcons
              provider={metadata.provider}
              castEnabled={isManaged}
              size={16}
              iconSplitter={
                <Divider orientation="vertical" variant="middle" flexItem />
              }
            />
          </Stack>
        </MetadataField>

        <MetadataField label="region" withDivider>
          <Typography
            variant="P14M"
            sx={{ color: tokens.theme.fg.structural.primary }}
          >
            {metadata.region}
          </Typography>
        </MetadataField>

        {summary?.clusterScore ? (
          <MetadataField label="SCORE" withDivider>
            <ScoreBadge score={summary.clusterScore} />
          </MetadataField>
        ) : null}

        <MetadataField label="KUBERNETES VERSION" withDivider>
          <Typography
            variant="P14M"
            sx={{ color: tokens.theme.fg.structural.primary }}
          >
            {metadata.k8sVersion}
          </Typography>
        </MetadataField>

        <MetadataField label="CONNECTED" withDivider>
          <Typography
            variant="P14M"
            sx={{ color: tokens.theme.fg.structural.primary }}
          >
            {metadata.connectedAt}
          </Typography>
        </MetadataField>

        <MetadataField label="CLUSTER ID" withDivider>
          <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
            <Typography
              variant="P14M"
              sx={{ color: tokens.theme.fg.structural.primary }}
              noWrap
            >
              {clusterId}
            </Typography>
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleCopyClusterId}
              testId="copy-cluster-id-metadata"
            >
              <Icons.Copy size={16} />
            </IconButton>
          </Stack>
        </MetadataField>

        <Stack
          justifyContent="center"
          alignItems="center"
          pl={12}
          sx={{
            borderLeft: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
          }}
        >
          <Button
            variant="text"
            size="md"
            onClick={openClusterDetailsDrawer}
            testId="cluster-metadata-view-all"
          >
            View all
            <Icons.ArrowRight size={20} />
          </Button>
        </Stack>
      </Box>
      {isKentManaged ? <KentrollerStatusSection /> : null}
    </LayoutCard>
  );
};
